'use client';

import CoreLink, { type PrefetchLinkProps } from '@eliadmin/core/src/components/link/prefetch-link';
import { forwardRef, type MouseEvent as ReactMouseEvent } from 'react';

const isPlainSelfClick = (event: ReactMouseEvent<HTMLAnchorElement>) =>
  !event.defaultPrevented &&
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey &&
  (!event.currentTarget.target || event.currentTarget.target === '_self');

const decodeFragment = (hash: string) => {
  const fragment = hash.replace(/^#/, '');

  try {
    return decodeURIComponent(fragment);
  } catch {
    return fragment;
  }
};

const handleSameDocumentFragment = (event: ReactMouseEvent<HTMLAnchorElement>) => {
  if (typeof window === 'undefined' || !isPlainSelfClick(event)) return;

  const targetUrl = new URL(event.currentTarget.href, window.location.href);
  const currentUrl = new URL(window.location.href);
  if (
    !targetUrl.hash ||
    targetUrl.origin !== currentUrl.origin ||
    targetUrl.pathname !== currentUrl.pathname ||
    targetUrl.search !== currentUrl.search
  ) {
    return;
  }

  const fragment = decodeFragment(targetUrl.hash);
  const target = document.getElementById(fragment) ?? document.getElementsByName(fragment)[0];
  if (!target) return;

  event.preventDefault();

  const nextHref = `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
  const currentHref = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
  if (nextHref !== currentHref) {
    window.history.pushState(null, '', nextHref);
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
};

/**
 * Core disables Next's cross-route scroll handling and replaces it with a long smooth scroll.
 * Keep explicit scroll={false} calls intact, but use Next's instant route scroll by default.
 * Same-document fragments are handled here so hash-only hrefs stay on the current route,
 * update browser history and scroll smoothly without overriding reduced-motion preferences.
 */
export const Link = forwardRef<HTMLAnchorElement, PrefetchLinkProps>(
  ({ onClick, ...props }, ref) => {
    const handleClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);
      handleSameDocumentFragment(event);
    };

    return <CoreLink {...props} ref={ref} scroll={props.scroll ?? true} onClick={handleClick} />;
  },
);

Link.displayName = 'Link';

export type { PrefetchLinkProps };
export default Link;
